import { TixologiResumedEvent, getTixologiEvents } from "./tixologi";
import createNoAuthSupabaseClient, { getUser } from "../supabase/supabase";
import {
  logException,
  logMessage,
  logSupabaseException,
} from "@punchup/sentry";

import { Comedian } from "./comedian";
import { Database } from "../supabase.types";
import { PostgrestError, SupabaseClient } from "@supabase/supabase-js";
import { VenueRow } from "./venues";
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjs from "@punchup/utils/dayjs";
import { sanitizeIlikeSearch } from "@punchup/utils/sanitizeIlikeSearch";
import { getApiClient } from "@punchup/utils";
import { getFollowedComedians } from "./follows";
import { isHttpUrl } from "@punchup/utils";

dayjs.extend(customParseFormat);

export type TicketFlags = Database["public"]["Enums"]["ticket_flags"];

export interface ShowInfo {
  id: string;
  comedian_id: string | null;
  datetime: dayjs.Dayjs;
  datetimeOriginal: string | null;
  location: string;
  venue: string;
  ticketLink: string;
  venueId: string;
  soldOut: boolean;
  metadataText: string;
  vipTicketLink: string;
  presaleCode: string;
  flags: TicketFlags[];
  title?: string;
  tixologiEventId: string | null;
  posterImg: string | null;
}

export type ShowInfoWithVenue = Omit<
  ShowInfo,
  "datetime" | "datetimeOriginal"
> & {
  datetime: string;
  venue: Partial<VenueRow>;
};

export type ShowRowWithVenue = Omit<ShowInfoRow, "venue"> & {
  venue: Partial<VenueRow>;
};

type ComedianShowRow = Database["public"]["Tables"]["comedian_shows"]["Row"];

/**
 * The show row as app code handles it. `deleted_at` is set only by
 * `softDeleteShow` and cleared by the upsert RPC — no app writer supplies it —
 * so a hand-built show (wizard, import, mock) need not carry it.
 */
export type ShowInfoRow = Omit<ComedianShowRow, "deleted_at"> &
  Partial<Pick<ComedianShowRow, "deleted_at">>;
export type ShowInfoWithComedianNameRow =
  Database["public"]["Views"]["comedian_shows_with_display"]["Row"];

export type ShowInfoInsert =
  Database["public"]["Tables"]["comedian_shows"]["Insert"];

export function processShowData(data: ShowInfoRow): ShowInfo {
  return {
    id: data.id,
    comedian_id: data.comedian_id,
    datetime: dayjs(data.datetime),
    datetimeOriginal: data.datetime,
    location: data.location,
    venue: data.venue,
    ticketLink: data.ticket_link,
    venueId: data.venue_id || "",
    soldOut: data.is_sold_out || false,
    metadataText: data.metadata_text || "",
    vipTicketLink: data.vip_ticket_link || "",
    presaleCode: data.presale_code || "",
    flags: (data.flags || []) as TicketFlags[],
    title: data.title || "",
    tixologiEventId: data.tixologi_event_id || null,
    posterImg: data.poster_img || null,
  };
}

export function processShowDataWithVenue(
  data: ShowInfoRow & { venue: Partial<VenueRow> }
): ShowInfoWithVenue {
  return {
    id: data.id,
    comedian_id: data.comedian_id,
    datetime: data.datetime || "",
    location: data.location,
    ticketLink: data.ticket_link,
    venueId: data.venue_id || "",
    soldOut: data.is_sold_out || false,
    metadataText: data.metadata_text || "",
    vipTicketLink: data.vip_ticket_link || "",
    venue: data.venue,
    presaleCode: data.presale_code || "",
    flags: (data.flags || []) as TicketFlags[],
    title: data.title || "",
    tixologiEventId: data.tixologi_event_id || null,
    posterImg: data.poster_img || null,
  };
}

/**
 * Returns all future shows for a given comedian.
 *
 * ** CLIENT SIDE ONLY! **
 *
 * Do not use this function in a server component.
 * Calling this on the server will use the wrong timezone and may
 * return incorrect results.
 *
 * Automatically takes care of pagination (keeping in mind Supabase's limit of 1000
 * records per query) in order to provide an easy to use API (just call one function
 * and get all shows) and avoiding to have to handle pagination in the front-end components.
 */
export async function CLIENT_getAllFutureShowsForComedian(
  comedianID: string,
  supabase?: SupabaseClient<Database>
): Promise<ShowInfo[]> {
  const allFutureShows = [];
  // Defines how many shows to fetch per query, 1000 is the maximum value for Supabase
  const PAGE_SIZE = 1000;
  let cursor = 0;
  let done = false;
  while (!done) {
    const venues = await CLIENT_getShowsRangeForComedian(
      comedianID,
      cursor,
      cursor + PAGE_SIZE - 1,
      false,
      supabase
    );
    allFutureShows.push(...venues);
    cursor += PAGE_SIZE;
    if (venues.length < PAGE_SIZE) {
      done = true;
    }
  }
  return allFutureShows as ShowInfo[];
}

/**
 * Fetches all future shows with venues for a given comedian.
 *
 * **CLIENT SIDE ONLY!**
 *
 * Do not use this function in a server component.
 * Calling this on the server will use the wrong timezone and may
 * return incorrect results.
 */
export async function CLIENT_getAllFutureShowsWithVenuesForComedian(
  comedianID: string,
  supabase?: SupabaseClient<Database>
): Promise<ShowRowWithVenue[]> {
  const allFutureShows = [];
  // Defines how many shows to fetch per query, 1000 is the maximum value for Supabase
  const PAGE_SIZE = 1000;
  let cursor = 0;
  let done = false;
  while (!done) {
    const venues = await CLIENT_getShowsRangeForComedian(
      comedianID,
      cursor,
      cursor + PAGE_SIZE - 1,
      true,
      supabase
    );
    allFutureShows.push(...venues);
    cursor += PAGE_SIZE;
    if (venues.length < PAGE_SIZE) {
      done = true;
    }
  }
  return allFutureShows as ShowRowWithVenue[];
}

/**
 * Returns the set of future show IDs for a comedian that are mapped to a
 * venue page (i.e. live in `venue_pages_shows`). Used by surfaces that focus
 * on a single comedian and want to skip shows that are managed elsewhere —
 * notably the internal tour-date editor, which only edits the focus
 * comedian's pairing and would otherwise risk overwriting lineup data on
 * venue-page-created shows.
 *
 * Filters via `show_comedian_pairings.comedian_id`, not the legacy
 * `comedian_shows.comedian_id`, so it stays correct for multi-comedian
 * shows where the legacy column denotes only one comedian.
 *
 * CLIENT SIDE ONLY — `datetime` filtering depends on the caller's timezone.
 */
export async function CLIENT_getVenuePageShowIdsForComedian(
  comedianId: string,
  supabase: SupabaseClient<Database>
): Promise<Set<string>> {
  const today = dayjs().format("YYYY-MM-DD");
  const { data } = await supabase
    .from("comedian_shows")
    .select(
      "id, show_comedian_pairings!inner(comedian_id), venue_pages_shows!inner(show_id)"
    )
    .eq("show_comedian_pairings.comedian_id", comedianId)
    .gte("datetime", today)
    .throwOnError();
  return new Set(data?.map((row) => row.id) ?? []);
}

export type ShowCreatorInfo = {
  entity_type: "comedian" | "venue_page" | "internal";
  entity_name: string | null;
};

/**
 * Fetches show IDs and creator info for shows where a comedian is paired
 * but did not create them.
 *
 * Uses the `get_external_show_creators_for_comedian` RPC which joins
 * show_comedian_pairings → show_creators and resolves entity names
 * in a single query.
 *
 * CLIENT SIDE ONLY — requires an authenticated Supabase client.
 */
export async function CLIENT_getExternalShowCreators(
  comedianId: string,
  supabase: SupabaseClient<Database>
): Promise<Map<string, ShowCreatorInfo>> {
  const { data } = await supabase
    .rpc("get_external_show_creators_for_comedian", {
      p_comedian_id: comedianId,
    })
    .throwOnError();

  const result = new Map<string, ShowCreatorInfo>();
  for (const row of data ?? []) {
    result.set(row.show_id, {
      entity_type: row.entity_type,
      entity_name: row.entity_name,
    });
  }
  return result;
}

/**
 * CLIENT SIDE ONLY!
 *
 * Do not use this function in a server component.
 * Calling this on the server will use the wrong timezone and may
 * return incorrect results.
 */
async function CLIENT_getShowsRangeForComedian(
  comedianID: string,
  start: number,
  end: number,
  withVenues: boolean = false,
  supabaseClient?: SupabaseClient<Database>
) {
  const supabase = supabaseClient || createNoAuthSupabaseClient();
  const today = dayjs().format("YYYY-MM-DD");

  if (withVenues) {
    const { data } = await supabase
      .from("comedian_shows")
      .select(
        "*, venue:comedian_shows_venue_id_fkey!inner (id, name, address, city, state, country, postal_code), show_comedian_pairings!inner(comedian_id)"
      )
      .eq("show_comedian_pairings.comedian_id", comedianID)
      .gte("datetime", today)
      .order("datetime")
      .range(start, end)
      .throwOnError();
    if (data) {
      return data;
    }
  } else {
    const { data } = await supabase
      .from("comedian_shows")
      .select("*, show_comedian_pairings!inner(comedian_id)")
      .eq("show_comedian_pairings.comedian_id", comedianID)
      .gte("datetime", today)
      .order("datetime")
      .range(start, end)
      .throwOnError();
    if (data) {
      return data.map((row) => processShowData(row));
    }
  }

  throw new Error(`Issue finding shows for ${comedianID}.`);
}

export type ShowsAndComediansPerforming = {
  comediansPerforming: {
    id: string;
    slug: string;
    display_name: string | null;
    about_img: string | null;
  }[];
  shows: (ShowInfo & { created_at: string | null })[];
};

export async function getShowsAndComediansCreatedAfterCertainDate(
  startDate: string
): Promise<ShowsAndComediansPerforming> {
  const supabase = createNoAuthSupabaseClient();
  const today = dayjs().format("YYYY-MM-DD");
  const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
  const { data, error } = await supabase
    .from("comedian_shows")
    .select(
      `
    *,
    comedian:comedians!comedian_shows_comedian_id_fkey!inner (
      id,
      slug,
      display_name,
      about_img
    )
    `
    )
    .gte("datetime", today)
    .gt("created_at", formattedStartDate)
    .order("comedian_id")
    .range(0, 999); // TODO remember to refactor this in order to support pagination

  if (error) {
    console.error(error);
  } else if (data) {
    const shows: (ShowInfo & { created_at: string | null })[] = [];
    const comediansPerforming: {
      id: string;
      slug: string;
      display_name: string | null;
      about_img: string | null;
    }[] = [];
    const alreadyAdded: string[] = [];

    data.forEach((row) => {
      if (row.comedian) {
        const comedianObj = {
          id: row.comedian?.id,
          slug: row.comedian?.slug,
          display_name: row.comedian?.display_name,
          about_img: row.comedian?.about_img,
        };
        if (!alreadyAdded.includes(row.comedian?.id)) {
          alreadyAdded.push(row.comedian?.id);
          comediansPerforming.push(comedianObj);
        }
      }
      shows.push({ ...processShowData(row), created_at: row.created_at });
    });
    return { comediansPerforming, shows };
  }
  throw new Error(`Issue finding shows for created after a certain date.`);
}

export async function getShow(showId: string): Promise<ShowInfo | null> {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from("comedian_shows")
    .select("*")
    .eq("id", showId)
    .limit(1)
    .maybeSingle();
  if (error) {
    console.error(error);
    throw new Error(`Failed to read show with id: ${showId}.`);
  } else if (data) {
    return processShowData(data);
  }
  return null;
}

export type ShowComedianPairing = {
  show_id: string;
  comedian_id: string;
  ordering: number;
  hidden_from_comedian_page: boolean;
};

export enum VenuePageShowVisibilityStatusEnum {
  VISIBLE = "visible",
  HIDDEN = "hidden",
  PENDING_REVIEW = "pending_review",
}

export type VenuePageShowPairing = {
  venue_page_id: string;
  show_id: string;
  show_visibility_status: VenuePageShowVisibilityStatusEnum;
};

export type ShowCreator = {
  creator_id: string;
  entity_type: "comedian" | "venue_page" | "internal";
  entity_id: string | null;
};

export type ShowCreatorAttribution = Pick<
  Database["public"]["Tables"]["show_creators"]["Row"],
  "show_id" | "entity_type" | "entity_id"
>;

/**
 * Reads the creator/ownership attribution for the given shows from
 * `show_creators` (one immutable row per show recording who created it).
 *
 * Runs through the passed client's RLS — comedian- and venue-dashboard admins
 * have SELECT on `show_creators` — so callers must pass an authenticated client,
 * not the no-auth one. Throws on a query error (callers should fail closed);
 * a show with no attribution row is simply omitted from the result.
 */
export async function getShowCreatorsForShows(
  supabase: SupabaseClient<Database>,
  showIds: string[]
): Promise<ShowCreatorAttribution[]> {
  if (showIds.length === 0) return [];

  const { data, error } = await supabase
    .from("show_creators")
    .select("show_id, entity_type, entity_id")
    .in("show_id", showIds);

  if (error) {
    logException(error, {
      data: `Error reading show_creators for shows: ${showIds.join(", ")}`,
    });
    throw new Error("Error reading show creator attribution");
  }

  return data ?? [];
}

/**
 * Creates or updates shows with their associated comedian and venue page relationships.
 *
 * @param supabase - Supabase client instance for database operations
 * @param shows - Array of show records to create or update
 * @param showComedianPairings - Array defining which comedians perform at which shows, with ordering and visibility settings on their page
 * @param venuePageShowPairings - Array defining which venue pages display which shows, with visibility status. Pass null to leave venue pairings unchanged.
 * @param callerIsServer - Whether this function is called from server-side code (affects authentication requirements)
 * @param showCreator - The user and entity that created the show. Only recorded for new shows (existing creators are preserved).
 */
export async function createOrUpdateShows({
  supabase,
  shows,
  showComedianPairings,
  venuePageShowPairings,
  callerIsServer,
  showCreator,
}: {
  supabase: SupabaseClient<Database>;
  shows: Omit<ShowInfoRow, "created_at">[];
  showComedianPairings: ShowComedianPairing[];
  venuePageShowPairings: VenuePageShowPairing[] | null;
  callerIsServer: boolean;
  showCreator: ShowCreator;
}) {
  // Reject `javascript:` / `data:` / other non-http(s) URLs at the write
  // boundary. ticket_link / vip_ticket_link are rendered into anchors and
  // passed to window.location.assign downstream, where a non-http scheme
  // would execute as script in the parent origin. Empty strings are
  // allowed (the Ticketmaster transformer falls back to "" when an
  // upstream event has no URL).
  for (const show of shows) {
    if (show.ticket_link && !isHttpUrl(show.ticket_link)) {
      return {
        error: new Error(
          `Invalid ticket_link for show ${show.id}: must be an http(s) URL`
        ),
      };
    }
    if (show.vip_ticket_link && !isHttpUrl(show.vip_ticket_link)) {
      return {
        error: new Error(
          `Invalid vip_ticket_link for show ${show.id}: must be an http(s) URL`
        ),
      };
    }
  }

  if (!callerIsServer) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      throw new Error(
        "Supabase client must have an active session to create/update a show."
      );
    }
  }

  const { error } = await supabase.rpc("create_update_shows_with_comedians", {
    show_records: shows,
    show_comedian_pairings: showComedianPairings,
    venue_page_show_pairings: venuePageShowPairings,
    show_creator: showCreator,
  });

  if (error) {
    console.error(error);
    logException(error, {
      data: `Error creating/updating shows with multiple comedians. Shows: ${JSON.stringify(shows)}`,
    });
    return { error: error };
  }

  return { error: null };
}

export type SoftDeleteShowResult =
  | {
      outcome: "deleted";
      show: Pick<
        Database["public"]["Tables"]["comedian_shows"]["Row"],
        "comedian_id"
      >;
    }
  | { outcome: "not_found" }
  | { outcome: "error"; error: PostgrestError };

/**
 * Soft-deletes a show: sets `deleted_at` instead of removing the row, so the
 * show (and every child row that used to cascade) can be restored. Who deleted
 * it is the manager audit log's record (`show.delete`), not the row's; the
 * deleted row's `comedian_id` comes back for that entry. `not_found` covers a
 * show that is already deleted. The caller logs the error outcome.
 *
 * `adminSupabase` must be the service-role client, used only after the caller
 * has authorized the delete. Postgres checks an UPDATE's new row against the
 * SELECT policies, and the `comedian_shows` SELECT policy
 * (`deleted_at IS NULL`) rejects the deleted row, so through an RLS-bound
 * client this always fails with 42501.
 */
export async function softDeleteShow(
  adminSupabase: SupabaseClient<Database>,
  show_id: string
): Promise<SoftDeleteShowResult> {
  const { data, error } = await adminSupabase
    .from("comedian_shows")
    .update({ deleted_at: dayjs().toISOString() })
    .eq("id", show_id)
    .is("deleted_at", null)
    .select("comedian_id")
    .maybeSingle();

  if (error) return { outcome: "error", error };
  return data ? { outcome: "deleted", show: data } : { outcome: "not_found" };
}

/**
 * Which of `showIds` are soft-deleted. `adminSupabase` must be the
 * service-role client: soft-deleted shows are hidden from every RLS-bound
 * client, so only the service role can answer. A failed read is logged and
 * answers "none".
 */
export async function findSoftDeletedShowIds(
  adminSupabase: SupabaseClient<Database>,
  showIds: string[]
): Promise<string[]> {
  if (showIds.length === 0) return [];
  const { data, error } = await adminSupabase
    .from("comedian_shows")
    .select("id")
    .in("id", showIds)
    .not("deleted_at", "is", null);
  if (error) {
    logException(error, {
      data: `Error looking up soft-deleted shows: ${showIds.join(", ")}`,
    });
    return [];
  }
  return (data ?? []).map((row) => row.id);
}

export async function changeShowVisibility({
  supabase,
  comedianId,
  show_id,
  shouldHide,
  source = "comedian-dashboard",
}: {
  supabase: SupabaseClient<Database>;
  comedianId: string;
  show_id: string;
  shouldHide: boolean;
  source?: string;
}): Promise<boolean> {
  const user = await getUser(supabase);
  if (!user) {
    throw new Error("Comedian manager is not logged in");
  }

  // ensuring that only a correct show-comedian pairing is updated is managed by the RLS policy
  await supabase
    .from("show_comedian_pairings")
    .update({
      hidden_from_comedian_page: shouldHide,
    })
    .eq("show_id", show_id)
    .eq("comedian_id", comedianId)
    .throwOnError();

  getApiClient()
    .post("/api/v2/manager/audit-log", {
      action: "show.visibility",
      resourceType: "show",
      resourceId: show_id,
      comedianId,
      metadata: { source, visible: !shouldHide },
    })
    .catch(logException);

  return true;
}

export async function changeVenuePageShowVisibility({
  supabase,
  venuePageId,
  show_id,
  shouldHide,
  source = "venue-dashboard",
}: {
  supabase: SupabaseClient<Database>;
  venuePageId: string;
  show_id: string;
  shouldHide: boolean;
  source?: string;
}): Promise<boolean> {
  const user = await getUser(supabase);
  if (!user) {
    throw new Error("Venue manager is not logged in");
  }

  const nextStatus = shouldHide
    ? VenuePageShowVisibilityStatusEnum.HIDDEN
    : VenuePageShowVisibilityStatusEnum.VISIBLE;

  await supabase
    .from("venue_pages_shows")
    .upsert(
      {
        venue_page_id: venuePageId,
        show_id,
        show_visibility_status: nextStatus,
      },
      { onConflict: "venue_page_id,show_id" }
    )
    .throwOnError();

  getApiClient()
    .post("/api/v2/manager/audit-log", {
      action: "venue_page_show.visibility",
      resourceType: "show",
      resourceId: show_id,
      venuePageId,
      metadata: { source, visible: !shouldHide },
    })
    .catch(logException);

  return true;
}

export async function getShowPairing(
  supabase: SupabaseClient<Database>,
  showId: string,
  comedianId: string
): Promise<ShowComedianPairing | null> {
  const { data } = await supabase
    .from("show_comedian_pairings")
    .select("*")
    .eq("show_id", showId)
    .eq("comedian_id", comedianId)
    .limit(1)
    .maybeSingle()
    .throwOnError();
  return data;
}

/**
 * Batch variant of `getShowPairing` — returns every pairing for each of the
 * given show IDs, grouped by show. Used by surfaces that edit only one
 * comedian's pairing but need to re-send the other comedians' pairings on
 * upsert, because the `create_update_shows_with_comedians` RPC treats the
 * pairings array as a set-replace: any pairing NOT in the new set is
 * deleted. Without a full read-back, editing a multi-comedian show through
 * a single-comedian surface silently drops the other lineup members.
 */
export async function getShowPairings(
  supabase: SupabaseClient<Database>,
  showIds: string[]
): Promise<Map<string, ShowComedianPairing[]>> {
  const grouped = new Map<string, ShowComedianPairing[]>();
  if (showIds.length === 0) return grouped;
  const { data } = await supabase
    .from("show_comedian_pairings")
    .select("*")
    .in("show_id", showIds)
    .throwOnError();
  for (const row of data ?? []) {
    const list = grouped.get(row.show_id) ?? [];
    list.push(row);
    grouped.set(row.show_id, list);
  }
  return grouped;
}

/**
 * Builds the `showComedianPairings` payload for surfaces that edit one
 * comedian's pairing on shows that may have other comedians too. For each
 * show, emits the focus comedian's pairing preserving its existing
 * `ordering` / `hidden_from_comedian_page` (defaults applied only when no
 * prior pairing exists, i.e. brand-new shows), plus every other
 * comedian's existing pairing unchanged.
 *
 * Pair this with `getShowPairings(supabase, showIds)` to fetch the input
 * map. Without the unchanged-others passthrough, the RPC's set-replace
 * semantics would delete every other lineup member.
 */
export function buildPairingsPreservingOthers({
  shows,
  focusComedianId,
  existingPairingsByShow,
}: {
  shows: { id: string }[];
  focusComedianId: string;
  existingPairingsByShow: Map<string, ShowComedianPairing[]>;
}): ShowComedianPairing[] {
  return shows.flatMap((show) => {
    const existing = existingPairingsByShow.get(show.id) ?? [];
    const focus = existing.find((p) => p.comedian_id === focusComedianId);
    const others = existing.filter((p) => p.comedian_id !== focusComedianId);
    return [
      {
        show_id: show.id,
        comedian_id: focusComedianId,
        ordering: focus?.ordering ?? 0,
        hidden_from_comedian_page: focus?.hidden_from_comedian_page ?? false,
      },
      ...others,
    ];
  });
}

export type ShowComedianRow = Comedian & {
  is_punchup_verified: boolean;
  ordering: number;
  hidden_from_comedian_page: boolean;
};

export type ShowWithComedian = ShowInfoRow & {
  comedian: Comedian | null;
  show_comedians: ShowComedianRow[];
  venue_pages: {
    id: string;
    slug: string;
    is_live: boolean;
    show_visibility_status?: VenuePageShowVisibilityStatusEnum;
    /** CDN image id of the venue page's logo; only the nearby path (getShows) selects it. */
    logo_img?: string | null;
  }[];
};

export type ShowWithTixologiEvent = ShowWithComedian & {
  event: TixologiResumedEvent | null;
  // Admin-only: true when the linked tixologi event is inactive (hidden from every
  // public surface, unsellable). Read live from bulk-availability by the admin show
  // fetcher, because the public /events read excludes inactive events entirely — so
  // `event` is null for an inactive show and the flag must live on the show itself.
  inactive?: boolean;
};

/**
 * The comedian a show is attributed to in comedian-oriented UI: the first
 * pairing in show_comedians. Deliberately ignores the deprecated
 * comedian_shows.comedian_id (the legacy `comedian` join).
 * Null for comedian-less shows (e.g. external venue-only shows), which the
 * /api/shows web path includes — callers that render a show under a comedian
 * must skip those instead of indexing show_comedians[0] directly.
 */
export function getPrimaryShowComedian(
  show: Pick<ShowWithComedian, "show_comedians">
): Comedian | null {
  return show.show_comedians?.[0] ?? null;
}

/**
 * Options for getShowsByLocationId
 */
export interface GetShowsByLocationIdOptions {
  /** Filter shows starting from this date (ISO string) */
  startDate?: string;
  /** Filter shows before this date, exclusive upper bound (ISO string) */
  endDate?: string;
  /**
   * Filter by followed comedians:
   * - true: only shows by followed comedians
   * - false: only shows by comedians not followed
   * - undefined: all shows
   */
  followedComedians?: boolean;
  /** Maximum number of shows to return. If not provided, returns all shows. */
  limit?: number;
  /** Number of shows to skip. Works independently of limit. */
  offset?: number;
  /**
   * Column to order results by.
   * - "comedian_id": Groups shows by comedian (default, maintains backwards compatibility)
   * - "datetime": Orders by show date/time
   */
  orderBy?: "comedian_id" | "datetime";
  /** Include shows that are not yet published (scheduled for future). Defaults to false. */
  includeNonPublicShows?: boolean;
}

/**
 * Get shows for a given location
 *
 * @param supabaseClient - Supabase client instance for database operations; must be authenticated if followedComedians is true
 * @param locationId - The venue/location ID to fetch shows for
 * @param options - Optional configuration for filtering, pagination, and ordering
 * @returns Array of shows with comedian and venue page information
 */
export async function getShowsByLocationId(
  supabaseClient: SupabaseClient<Database>,
  locationId: string,
  options: GetShowsByLocationIdOptions = {}
): Promise<ShowWithComedian[]> {
  const {
    startDate,
    endDate,
    followedComedians,
    limit,
    offset,
    orderBy = "comedian_id", // Default to comedian_id for backwards compatibility
    includeNonPublicShows,
  } = options;

  let query = supabaseClient
    .from("comedian_shows")
    .select(
      `
      *,
      comedian:comedians!comedian_shows_comedian_id_fkey(
        *
      ),
      show_comedians:show_comedian_pairings(
        ordering,
        hidden_from_comedian_page,
        comedian:comedians!show_comedian_pairings_comedian_id_fkey(
          *
        )
      ),
      venue_pages_shows!left(
        venue_pages!inner(
          id,
          slug,
          is_live
        )
      )
    `
    )
    .eq("venue_id", locationId)
    .order(orderBy, { ascending: true });

  if (startDate) {
    query.gte("datetime", startDate);
  }
  if (endDate) {
    query.lt("datetime", endDate);
  }
  if (followedComedians != undefined) {
    const comedianIds = (
      await getFollowedComedians({
        supabase: supabaseClient,
        callerIsServer: true,
      })
    ).map((comedian) => comedian.comedian_id);
    if (followedComedians) {
      query.in("comedian_id", comedianIds);
    } else {
      query.not("comedian_id", "in", comedianIds);
    }
  }
  // Apply pagination with range
  // Both limit and offset work independently
  const start = offset ?? 0;
  if (limit !== undefined && limit > 0) {
    const end = start + limit - 1;
    query = query.range(start, end);
  } else if (start > 0) {
    // Apply offset without limit by using a large upper bound
    // Supabase requires both start and end for range()
    query = query.range(start, start + 999999);
  }
  const { data } = await query.throwOnError();
  if (data === null) {
    throw new Error(
      `Unable to retrieve shows for venue ${locationId}. Database returned null instead of an empty array.`
    );
  }

  // Filter out shows with a future published_at (scheduled tour dates not yet live)
  // Shows with null published_at are immediately visible (backwards compatible)
  // This applies regardless of includeNonPublicShows, since scheduled publishing
  // is independent of comedian visibility settings.
  const filteredData = data.filter(
    (show) => !show.published_at || !dayjs().isBefore(show.published_at)
  );

  return filteredData.map((show) => {
    const {
      show_comedians: _,
      comedian: __,
      venue_pages_shows: ___,
      ...showWithoutUnwantedAttributes
    } = show;
    return {
      ...showWithoutUnwantedAttributes,
      comedian: show.comedian?.id
        ? ({
            id: show.comedian?.id,
            display_name: show.comedian?.display_name || "",
            display_name_short: show.comedian?.display_name_short || "",
            slug: show.comedian?.slug || "",
            about_img: show.comedian?.about_img || null,
            is_punchup_verified: show.comedian?.is_punchup_verified || false,
          } as Comedian)
        : null,
      show_comedians: show.show_comedians
        .map((sc) => ({
          ...sc.comedian,
          ordering: sc.ordering || 0,
          hidden_from_comedian_page: sc.hidden_from_comedian_page || false,
        }))
        .sort((a, b) => a.ordering - b.ordering),
      venue_pages:
        show.venue_pages_shows?.map((vps) => ({
          id: vps.venue_pages.id,
          slug: vps.venue_pages.slug,
          is_live: vps.venue_pages.is_live,
        })) || [],
    };
  }) as ShowWithComedian[];
}

export interface GetShowsByLocationIdsOptions {
  startDate?: string;
  endDate?: string;
  followedComedians?: boolean;
  limit?: number;
  offset?: number;
  includeNonPublicShows?: boolean;
}

/**
 * Get shows for a set of location IDs with deterministic datetime+id ordering.
 * Use this for multi-location aggregations (homepage feed, comedian-aggregation
 * paths). For "shows on a specific venue page" use {@link getShowsForVenuePage}.
 */
export async function getShowsByLocationIds(
  supabaseClient: SupabaseClient<Database>,
  locationIds: string[],
  options: GetShowsByLocationIdsOptions = {}
): Promise<ShowWithComedian[]> {
  const { startDate, endDate, followedComedians, limit, offset } = options;

  let query = supabaseClient
    .from("comedian_shows")
    .select(
      `
      *,
      comedian:comedians!comedian_shows_comedian_id_fkey(
        *
      ),
      show_comedians:show_comedian_pairings(
        ordering,
        hidden_from_comedian_page,
        comedian:comedians!show_comedian_pairings_comedian_id_fkey(
          *
        )
      ),
      venue_pages_shows!left(
        show_visibility_status,
        venue_pages!inner(
          id,
          slug,
          is_live
        )
      )
    `
    )
    .order("datetime", { ascending: true })
    .order("id", { ascending: true })
    .in("venue_id", locationIds);

  if (startDate) {
    query = query.gte("datetime", startDate);
  }
  if (endDate) {
    query = query.lt("datetime", endDate);
  }
  if (followedComedians != undefined) {
    const comedianIds = (
      await getFollowedComedians({
        supabase: supabaseClient,
        callerIsServer: true,
      })
    ).map((comedian) => comedian.comedian_id);
    if (followedComedians) {
      query = query.in("comedian_id", comedianIds);
    } else {
      query = query.not("comedian_id", "in", comedianIds);
    }
  }

  const start = offset ?? 0;
  if (limit !== undefined && limit > 0) {
    query = query.range(start, start + limit - 1);
  } else if (start > 0) {
    query = query.range(start, start + 999999);
  }

  const { data } = await query.throwOnError();
  if (data === null) {
    throw new Error(
      `Unable to retrieve shows for locations [${locationIds.join(", ")}]. Database returned null instead of an empty array.`
    );
  }

  return mapToShowWithComedian(data as RawShowRow[]);
}

export interface GetShowsForVenuePageOptions {
  startDate?: string;
  endDate?: string;
  followedComedians?: boolean;
  limit?: number;
  offset?: number;
  includeNonPublicShows?: boolean;
  /** Case-insensitive title match; sanitized for PostgREST before use. */
  search?: string;
}

/**
 * Get shows linked to a specific venue page via `venue_pages_shows`.
 *
 * Use this for everything that's "shows on this venue page" — public venue
 * pages, calendar, producer pages, show group lookups. Filters by linkage
 * (not by `venue_id IN location_ids`), so producer-page shows from venues
 * outside the page's location_ids are included.
 */
export async function getShowsForVenuePage(
  supabaseClient: SupabaseClient<Database>,
  venuePageId: string,
  options: GetShowsForVenuePageOptions = {}
): Promise<ShowWithComedian[]> {
  const { startDate, endDate, followedComedians, limit, offset, search } =
    options;

  // Two embeds of venue_pages_shows: the unaliased `!left` returns the full
  // list of venue pages each show is linked to (for the payload), while the
  // aliased `filter_vps:venue_pages_shows!inner` constrains the result set to
  // shows linked to the given venue page and additionally drops links whose
  // visibility status is HIDDEN.
  // The select string is kept as an inline literal so Supabase can infer the
  // response shape — building one dynamically widens `data` to GenericStringError.
  let query = supabaseClient
    .from("comedian_shows")
    .select(
      `
      *,
      comedian:comedians!comedian_shows_comedian_id_fkey(
        *
      ),
      show_comedians:show_comedian_pairings(
        ordering,
        hidden_from_comedian_page,
        comedian:comedians!show_comedian_pairings_comedian_id_fkey(
          *
        )
      ),
      venue_pages_shows!left(
        show_visibility_status,
        venue_pages!inner(
          id,
          slug,
          is_live
        )
      ),
      filter_vps:venue_pages_shows!inner(venue_page_id, show_visibility_status)
    `
    )
    .order("datetime", { ascending: true })
    .order("title", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: true })
    .order("id", { ascending: true })
    .eq("filter_vps.venue_page_id", venuePageId)
    .neq(
      "filter_vps.show_visibility_status",
      VenuePageShowVisibilityStatusEnum.HIDDEN
    );

  const searchTerm = search ? sanitizeIlikeSearch(search) : "";
  if (searchTerm) {
    query = query.ilike("title", `%${searchTerm}%`);
  }

  if (startDate) {
    query = query.gte("datetime", startDate);
  }
  if (endDate) {
    query = query.lt("datetime", endDate);
  }
  if (followedComedians != undefined) {
    const comedianIds = (
      await getFollowedComedians({
        supabase: supabaseClient,
        callerIsServer: true,
      })
    ).map((comedian) => comedian.comedian_id);
    if (followedComedians) {
      query = query.in("comedian_id", comedianIds);
    } else {
      query = query.not("comedian_id", "in", comedianIds);
    }
  }

  const start = offset ?? 0;
  if (limit !== undefined && limit > 0) {
    query = query.range(start, start + limit - 1);
  } else if (start > 0) {
    query = query.range(start, start + 999999);
  }

  const { data } = await query.throwOnError();
  if (data === null) {
    throw new Error(
      `Unable to retrieve shows for venue page ${venuePageId}. Database returned null instead of an empty array.`
    );
  }

  return mapToShowWithComedian(data as RawShowRow[]);
}

type RawShowRow = ShowInfoRow & {
  comedian: (Partial<Comedian> & { id?: string | null }) | null;
  show_comedians: Array<{
    ordering: number | null;
    hidden_from_comedian_page: boolean | null;
    comedian: Comedian;
  }>;
  venue_pages_shows: Array<{
    show_visibility_status: string | null;
    venue_pages: { id: string; slug: string; is_live: boolean };
  }> | null;
  filter_vps?: unknown;
};

/**
 * Shared post-fetch shaping for {@link getShowsByLocationIds} and
 * {@link getShowsForVenuePage}: filter out shows scheduled for the future
 * via `published_at`, then reshape the row's embeds into a `ShowWithComedian`.
 * The aliased `filter_vps` field is discarded — it only exists to constrain
 * the query and is not part of the public type.
 */
function mapToShowWithComedian(rows: RawShowRow[]): ShowWithComedian[] {
  const filtered = rows.filter(
    (show) => !show.published_at || !dayjs().isBefore(show.published_at)
  );
  return filtered.map((show) => {
    const {
      show_comedians: _,
      comedian: __,
      venue_pages_shows: ___,
      filter_vps: ____,
      ...showWithoutUnwantedAttributes
    } = show;
    return {
      ...showWithoutUnwantedAttributes,
      comedian: show.comedian?.id
        ? ({
            id: show.comedian.id,
            display_name: show.comedian.display_name || "",
            display_name_short: show.comedian.display_name_short || "",
            slug: show.comedian.slug || "",
            about_img: show.comedian.about_img || null,
            is_punchup_verified: show.comedian.is_punchup_verified || false,
          } as Comedian)
        : null,
      show_comedians: show.show_comedians
        .map((sc) => ({
          ...sc.comedian,
          ordering: sc.ordering || 0,
          hidden_from_comedian_page: sc.hidden_from_comedian_page || false,
        }))
        .sort((a, b) => a.ordering - b.ordering),
      venue_pages:
        show.venue_pages_shows?.map((vps) => ({
          id: vps.venue_pages.id,
          slug: vps.venue_pages.slug,
          is_live: vps.venue_pages.is_live,
          show_visibility_status:
            (vps.show_visibility_status as VenuePageShowVisibilityStatusEnum) ??
            undefined,
        })) || [],
    };
  }) as ShowWithComedian[];
}

/**
 * Get nearby shows for a given location
 * [Client or Server component]
 * @param locations A list of location IDs to fetch shows for (formerly known as venue_ids)
 * @param limitDays [Optional] Limit of how many days in the future to look for shows
 * @param comedianId [Optional] Filter shows by comedian
 * @param followedComedians [Optional] Filter shows by followed comedians
 *  - true: only shows by followed comedians
 *  - false: only shows by comedians not followed
 *  - undefined: all shows
 * @param includeNonPublicShows [Optional] Include shows that are not public, aka shows of non published comedians AND external preview
 *  - true: include non public shows
 *  - false: only include public shows
 *  - undefined: only include public shows
 * @returns
 */
export async function getShows(
  supabase: SupabaseClient<Database>,
  {
    locations,
    limitDays,
    comedianId,
    followedComedians,
    includeNonPublicShows,
    fetchFromDatetime,
  }: {
    locations?: string[];
    limitDays?: number;
    comedianId?: string;
    followedComedians?: boolean;
    includeNonPublicShows?: boolean;
    fetchFromDatetime?: string;
  }
): Promise<ShowWithComedian[]> {
  if (locations && locations.length === 0) {
    return [];
  }

  const fetchFrom =
    fetchFromDatetime && dayjs(fetchFromDatetime).isValid()
      ? dayjs(fetchFromDatetime).toISOString()
      : dayjs().subtract(12, "hours").toISOString();

  let baseQuery = supabase
    .from("comedian_shows")
    .select(
      `*,
      comedian:comedians!comedian_shows_comedian_id_fkey (
        *
      ),
      show_comedians:show_comedian_pairings(
        ordering,
        hidden_from_comedian_page,
        comedian:comedians!show_comedian_pairings_comedian_id_fkey(
          *
        )
      ),
      venue_pages_shows!left(
        show_visibility_status,
        venue_pages!inner(
          id,
          slug,
          is_live,
          logo_img
        )
      )`
    )
    .gte("datetime", fetchFrom)
    .order("datetime", { ascending: true });

  if (locations) {
    baseQuery = baseQuery.in("venue_id", locations);
  }

  if (limitDays) {
    baseQuery = baseQuery.lte(
      "datetime",
      dayjs().add(limitDays, "days").format("YYYY-MM-DD")
    );
  }
  if (comedianId && followedComedians !== undefined) {
    // Help devs catch that this is invalid combination
    throw new Error("Cannot filter by both comedianId and followedComedians");
  }
  if (comedianId) {
    // Use show_comedian_pairings (junction table) instead of the deprecated
    // comedian_shows.comedian_id column. A pre-query fetches matching show IDs
    // so the main select still LEFT-joins all pairings (needed by the
    // showCanBeRendered visibility check below).
    //
    // The pre-query mirrors the main query's date window via an !inner join
    // on comedian_shows. Without this, pairedShowIds would include the
    // comedian's full historical pairings — for prolific comedians (600+
    // shows) the resulting `.in("id", ...)` URL exceeds Node/Supabase fetch
    // size limits and the request fails with a generic "fetch failed".
    let pairedQuery = supabase
      .from("show_comedian_pairings")
      .select("show_id, comedian_shows!inner(datetime)")
      .eq("comedian_id", comedianId)
      .gte("comedian_shows.datetime", fetchFrom);

    if (limitDays) {
      pairedQuery = pairedQuery.lte(
        "comedian_shows.datetime",
        dayjs().add(limitDays, "days").format("YYYY-MM-DD")
      );
    }

    const { data: pairedRows } = await pairedQuery;

    const pairedShowIds = pairedRows?.map((r) => r.show_id) ?? [];
    if (pairedShowIds.length === 0) return [];

    baseQuery = baseQuery.in("id", pairedShowIds);
  }
  if (followedComedians !== undefined) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      const comedianIds = (
        await getFollowedComedians({
          supabase: supabase,
          callerIsServer: true,
        })
      ).map((comedian) => comedian.comedian_id);
      if (followedComedians) {
        baseQuery.in("comedian_id", comedianIds);
      } else {
        baseQuery.not("comedian_id", "in", comedianIds);
      }
    } else {
      logMessage("User is not logged in, skipping followedComedians filter", {
        level: "warning",
      });
    }
  }

  const { data, error } = await baseQuery.order("datetime", {
    ascending: true,
  });
  if (error) {
    logSupabaseException(error, "getShows from comedian_shows", {
      tags: {
        comedianId,
        followedComedians,
        includeNonPublicShows,
        fetchFromDatetime,
      },
    });
    throw new Error("Error fetching shows");
  }

  const shows = data?.filter((show) => {
    // Filter out shows with a future published_at (scheduled tour dates not yet live)
    // Shows with null published_at are immediately visible (backwards compatible)
    // This check applies regardless of includeNonPublicShows, since scheduled
    // publishing is independent of comedian visibility settings.
    if (show.published_at && dayjs().isBefore(show.published_at)) {
      return false;
    }

    // Case 1: Return all shows regardless of the comedian's publishing status
    // This is used when we want to display shows of non published comedians AND external preview, for instance
    if (includeNonPublicShows) {
      return true;
    }

    // Case 2: return only shows that should be visible by the public
    // i.e. shows where all the comedians are:
    // if they are punchup_verified: they must be published and not an external preview
    // whereas if they are not punchup verified, the show should be visible.
    // The alternative way to do this is to check that at least one comedian passes these conditions instead of all

    // Helper function to check if a comedian should be visible
    const showCanBeRendered = (comedian: Comedian) => {
      // If comedian is Punchup verified, they must be published and not an external preview
      // for the show to be displayed
      if (comedian.is_punchup_verified) {
        return (
          !comedian.is_external_preview &&
          dayjs().isAfter(comedian.published_at)
        );
      }

      // If the comedian is not Punchup verified, the show will be visible in any case
      return true;
    };

    // Check all show_comedians (if any exist)
    if (show.show_comedians && show.show_comedians.length > 0) {
      // The check must pass for all comedians associated to this show for the show to be visible
      const allComediansVisible = show.show_comedians.every((sc) =>
        showCanBeRendered(sc.comedian as Comedian)
      );
      if (!allComediansVisible) return false;
    }

    // Shows with no comedians are allowed through. If you want to filter those
    // you need to do that separately.
    return true;
  });
  if (!shows) {
    return [];
  }

  return shows.map((show) => {
    const {
      show_comedians: _,
      comedian: __,
      venue_pages_shows: ___,
      ...showWithoutUnwantedAttributes
    } = show;
    return {
      ...showWithoutUnwantedAttributes,
      comedian: show.comedian?.id
        ? ({
            id: show.comedian.id,
            display_name: show.comedian?.display_name || "",
            display_name_short: show.comedian?.display_name_short || "",
            slug: show.comedian?.slug || "",
            about_img: show.comedian?.about_img || null,
            is_punchup_verified: show.comedian?.is_punchup_verified || false,
          } as Comedian)
        : null,
      show_comedians: show.show_comedians
        .map((sc) => ({
          ...sc.comedian,
          ordering: sc.ordering || 0,
          hidden_from_comedian_page: sc.hidden_from_comedian_page || false,
        }))
        .sort((a, b) => a.ordering - b.ordering),
      venue_pages:
        show.venue_pages_shows?.map((vps) => ({
          id: vps.venue_pages.id,
          slug: vps.venue_pages.slug,
          is_live: vps.venue_pages.is_live,
          show_visibility_status: vps.show_visibility_status,
          logo_img: vps.venue_pages.logo_img,
        })) || [],
    };
  }) as ShowWithComedian[];
}

export async function getShowById(
  showId: string
): Promise<ShowWithComedian | null> {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from("comedian_shows")
    .select(
      `
      *,
      comedian:comedians!comedian_shows_comedian_id_fkey(
        *
      ),
      show_comedians:show_comedian_pairings(
        ordering,
        hidden_from_comedian_page,
        comedian:comedians!show_comedian_pairings_comedian_id_fkey(
          *
        )
      ),
      venue_pages_shows!left(
        show_visibility_status,
        venue_pages!inner(
          id,
          slug,
          is_live
        )
      )
      `
    )
    .eq("id", showId)
    .maybeSingle();

  if (error) {
    logException(error, { data: `Error reading show with id: ${showId}` });
    throw error;
  }
  if (!data) {
    return null;
  }

  return {
    ...data,
    comedian: data.comedian?.id ? (data.comedian as Comedian) : null,
    show_comedians: data.show_comedians.map((sc) => ({
      ...(sc.comedian as Comedian & { is_punchup_verified: boolean }),
      ordering: sc.ordering || 0,
      hidden_from_comedian_page: sc.hidden_from_comedian_page || false,
    })),
    venue_pages:
      data.venue_pages_shows?.map((vps) => ({
        id: vps.venue_pages.id,
        slug: vps.venue_pages.slug,
        is_live: vps.venue_pages.is_live,
        show_visibility_status:
          (vps.show_visibility_status as VenuePageShowVisibilityStatusEnum) ??
          undefined,
      })) || [],
  };
}

/**
 * Batch fetch multiple shows by their IDs
 * More efficient than calling getShowById multiple times
 */
export async function getShowsByIds(
  showIds: string[]
): Promise<Map<string, ShowWithComedian>> {
  if (showIds.length === 0) {
    return new Map();
  }

  const supabase = createNoAuthSupabaseClient();
  const { data } = await supabase
    .from("comedian_shows")
    .select(
      `
      *,
      comedian:comedians!comedian_shows_comedian_id_fkey(
        *
      ),
      show_comedians:show_comedian_pairings(
        ordering,
        hidden_from_comedian_page,
        comedian:comedians!show_comedian_pairings_comedian_id_fkey(
          *
        )
      ),
      venue_pages_shows!left(
        show_visibility_status,
        venue_pages!inner(
          id,
          slug,
          is_live
        )
      )
      `
    )
    .in("id", showIds)
    .throwOnError();

  if (!data) {
    return new Map();
  }

  const showsMap = new Map<string, ShowWithComedian>();

  for (const row of data) {
    const show: ShowWithComedian = {
      ...row,
      comedian: row.comedian?.id ? (row.comedian as Comedian) : null,
      show_comedians: row.show_comedians.map((sc) => ({
        ...(sc.comedian as Comedian & { is_punchup_verified: boolean }),
        ordering: sc.ordering || 0,
        hidden_from_comedian_page: sc.hidden_from_comedian_page || false,
      })),
      venue_pages:
        row.venue_pages_shows?.map((vps) => ({
          id: vps.venue_pages.id,
          slug: vps.venue_pages.slug,
          is_live: vps.venue_pages.is_live,
          show_visibility_status:
            (vps.show_visibility_status as VenuePageShowVisibilityStatusEnum) ??
            undefined,
        })) || [],
    };
    showsMap.set(row.id, show);
  }

  return showsMap;
}
